/**
 * Validation utility functions for form inputs
 */

/**
 * Validate Indian Aadhar card number
 * Must be exactly 12 digits
 */
export const validateAadhar = (value) => {
  if (!value) return true;
  const trimmed = value.trim().replace(/\s/g, "");
  if (!/^\d+$/.test(trimmed)) return "Aadhar number must contain only digits";
  if (trimmed.length !== 12) return "Aadhar number must be exactly 12 digits";
  return true;
};

/**
 * Validate Indian PAN card number
 * Format: 5 letters, 4 digits, 1 letter (e.g., ABCDE1234F)
 */
export const validatePAN = (value) => {
  const trimmedValue = value.trim().toUpperCase();

  if (trimmedValue.length !== 10) {
    return "PAN must be exactly 10 characters";
  }

  if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(trimmedValue)) {
    return "Invalid PAN format. Format: 5 letters, 4 digits, 1 letter (e.g., ABCDE1234F)";
  }

  return true;
};

/**
 * Validate Indian GST number
 * Format: 2 digits, 5 letters, 4 digits, 1 letter, 1 alphanumeric, Z, 1 alphanumeric
 */
export const validateGST = (value) => {
  if (!value) return true; // Optional field

  const trimmedValue = value.trim().toUpperCase();

  if (trimmedValue.length !== 15) {
    return "GST number must be exactly 15 characters";
  }

  if (
    !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(
      trimmedValue,
    )
  ) {
    return "Invalid GST format. Format: 2 digits, 5 letters, 4 digits, 1 letter, 1 alphanumeric, Z, 1 alphanumeric";
  }

  return true;
};

/**
 * Validate Indian mobile number
 * Must be 10 digits and start with 6, 7, 8, or 9
 */
export const validateMobile = (value) => {
  const trimmedValue = value.trim();

  if (!/^\d+$/.test(trimmedValue)) {
    return "Mobile number must contain only digits";
  }

  if (trimmedValue.length !== 10) {
    return "Mobile number must be exactly 10 digits";
  }

  return true;
};

export const validateEmail = (value) => {
  const trimmedValue = value.trim().toLowerCase();

  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedValue)) {
    return "Invalid email format. Use format: user@example.com";
  }

  if (trimmedValue.length > 254) {
    return "Email address is too long";
  }

  if (!/^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/.test(trimmedValue)) {
    return "Email contains invalid characters";
  }

  return true;
};

export const validateIFSC = (value) => {
  if (!value) return true; // Optional field

  const trimmedValue = value.trim().toUpperCase();

  if (trimmedValue.length !== 11) {
    return "IFSC code must be exactly 11 characters";
  }

  if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(trimmedValue)) {
    return "Invalid IFSC format. Format: 4 letters, 0, 6 alphanumeric (e.g., SBIN0001234)";
  }

  return true;
};

export const validateAccountNumber = (value) => {
  if (!value) return true; // Optional field

  const trimmedValue = value.trim();

  if (!/^\d+$/.test(trimmedValue)) {
    return "Account number must contain only digits";
  }

  if (trimmedValue.length < 9 || trimmedValue.length > 18) {
    return "Account number must be between 9 and 18 digits";
  }

  return true;
};

/**
 * Validate required field
 */
export const validateRequired = (value, fieldLabel) => {
  if (!value || (typeof value === "string" && value.trim() === "")) {
    return `${fieldLabel} is required`;
  }
  return true;
};

/**
 * Sanitize and format input values
 */
export const sanitizeInput = {
  // Convert to uppercase for PAN, GST, IFSC
  toUpperCase: (value) => {
    return typeof value === "string" ? value.toUpperCase() : value;
  },

  // Remove non-numeric characters
  numbersOnly: (value) => {
    return typeof value === "string" ? value.replace(/\D/g, "") : value;
  },

  // Trim whitespace
  trim: (value) => {
    return typeof value === "string" ? value.trim() : value;
  },

  // Convert to lowercase for email
  toLowerCase: (value) => {
    return typeof value === "string" ? value.toLowerCase() : value;
  },
};
